// Telefonie-Routen (API.md Abschnitt 11 plus Zusätze des Moduls). Nutzt den gemeinsamen HTTP-Client.
import { http } from "../api/client";
import { telefonieApi, type Anruf, type AnrufStart, type PreflightErgebnis, type TelefonieToken } from "../api";
import type { DialerAnsicht, DialerModus, DialerNaechster, DialerSitzung, EinstellungenVoll, StatusInfo } from "./typen";

const k = encodeURIComponent;

export interface Nummer {
  id: string;
  number: string;
  label: string | null;
  provider: string;
  user_id: string | null;
  is_group: boolean;
  twilio_status: string | null;
}

export interface KonfigurationVoll {
  account_sid: string | null;
  api_key_sid: string | null;
  twiml_app_sid: string | null;
  has_secret: boolean;
  has_auth_token: boolean;
  edge: string | null;
  edge_ausweich: string | null;
  vollstaendig: boolean;
  fehlend: string[];
  quelle: Record<string, string>;
  oeffentliche_url: string | null;
  voice_url: string;
  status_url: string;
  hinweise?: string[];
}

export const tel = {
  ...telefonieApi,
  status: () => http.get<StatusInfo>("/api/telefonie/status"),
  token: (relay = false) =>
    http.post<TelefonieToken & { ice_servers?: RTCIceServer[] | null }>(`/api/telefonie/token${relay ? "?relay=1" : ""}`),
  anrufStarten: (eingabe: {
    to: string;
    lead_id: string;
    contact_id?: string | null;
    bestaetigt?: boolean;
    dialer_sitzung?: string | null;
    klingeldauer?: number | null;
    /** false = Netz-Freizeichen (Early Media) für den Kunden nicht anfordern (eigener Wählton gewählt) */
    early_media?: boolean | null;
  }) => http.post<AnrufStart>("/api/telefonie/anrufe", eingabe),
  /** Offener Anruf des Nutzers (nach Neuladen der Seite oder Neustart des Dienstes), sonst null */
  aktiverAnruf: () => http.get<{ call: Anruf | null }>("/api/telefonie/anrufe/aktiv"),
  auflegen: (callId: string) => http.post<{ call: Anruf }>(`/api/telefonie/anrufe/${k(callId)}/auflegen`),
  handy: (callId: string) => http.post<{ call: Anruf }>(`/api/telefonie/anrufe/${k(callId)}/handy`),
  dtmf: (callId: string, digits: string) => http.post<{ call: Anruf }>(`/api/telefonie/anrufe/${k(callId)}/dtmf`, { digits }),
  closeNachtragen: (callId: string) => http.post<{ call: Anruf }>(`/api/telefonie/anrufe/${k(callId)}/close-nachtragen`),
  nummerPruefen: (nummer: string) =>
    http.get<{ gueltig: boolean; code: string | null; hinweis: string | null; e164: string | null; formatiert?: string }>(
      "/api/telefonie/nummer-pruefen",
      { nummer },
    ),
  preflightSpeichern: (ergebnis: PreflightErgebnis) => http.post<{ preflight: PreflightErgebnis }>("/api/telefonie/preflight", ergebnis),
  einstellungenLesen: (userId?: string | null) => http.get<EinstellungenVoll>("/api/telefonie/einstellungen", { user_id: userId ?? undefined }),
  einstellungenAendern: (aenderung: Partial<EinstellungenVoll>, userId?: string | null) =>
    http.put<EinstellungenVoll>(`/api/telefonie/einstellungen${userId ? `?user_id=${k(userId)}` : ""}`, aenderung),
  nummern: (userId?: string | null, pruefen = false) =>
    http.get<{ data: Nummer[] }>("/api/telefonie/nummern", { user_id: userId ?? undefined, pruefen: pruefen || undefined }),
  konfigurationLesen: () => http.get<KonfigurationVoll>("/api/telefonie/konfiguration"),
  konfigurationAendern: (k_: Record<string, string | boolean | null>) => http.put<KonfigurationVoll>("/api/telefonie/konfiguration", k_),
  /** Notiz und Ergebnis der Anrufkarte (auch während des Gesprächs, beendet nichts). */
  anrufNotiz: (callId: string, eingabe: { note_html?: string | null; disposition?: string | null }) =>
    http.post<{ call: Anruf }>(`/api/telefonie/anrufe/${k(callId)}/notiz`, eingabe),
  dialerStarten: (smart_view_id: string, o: { klingeldauer?: number | null; modus?: DialerModus; kurz?: boolean | null } = {}) =>
    http.post<{ sitzung: DialerSitzung }>("/api/telefonie/dialer/sitzungen", {
      smart_view_id,
      klingeldauer: o.klingeldauer ?? null,
      modus: o.modus ?? "von_vorn",
      kurz: o.kurz ?? null,
    }),
  dialerAktiv: () => http.get<{ sitzung: DialerSitzung | null }>("/api/telefonie/dialer/sitzungen/aktiv"),
  /** vorladen: den übernächsten Lead schon suchen und reservieren (auch während eines Gesprächs). */
  dialerNaechster: (id: string, vorladen = false) =>
    http.post<DialerNaechster>(`/api/telefonie/dialer/sitzungen/${k(id)}/naechster${vorladen ? "?vorladen=1" : ""}`),
  /** Für „Verwalten“ an der Smart View: Anrufe heute, wer ruft an, Klingeldauern. */
  dialerAnsicht: (viewId: string) => http.get<DialerAnsicht>(`/api/telefonie/dialer/ansichten/${k(viewId)}`),
  dialerEnde: (id: string) => http.post<{ sitzung: DialerSitzung }>(`/api/telefonie/dialer/sitzungen/${k(id)}/ende`),
};
